import { Component } from '@theme/component';
import { updateAllHeaderCustomProperties } from '@theme/utilities';

/**
 * A countdown timer bar that hides itself once the target date has passed.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 *
 * @extends {Component<Refs>}
 */
class CountdownBarComponent extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  /** @type {number | undefined} */
  #intervalId;

  connectedCallback() {
    super.connectedCallback();

    this.#tick();
    this.#intervalId = window.setInterval(this.#tick, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#intervalId);
  }

  #tick = () => {
    const end = new Date(this.dataset.end ?? '').getTime();
    const remaining = end - Date.now();

    if (Number.isNaN(end) || remaining <= 0) {
      window.clearInterval(this.#intervalId);
      this.#hide();
      return;
    }

    const days = Math.floor(remaining / 86_400_000);
    const hours = Math.floor((remaining % 86_400_000) / 3_600_000);
    const minutes = Math.floor((remaining % 3_600_000) / 60_000);
    const seconds = Math.floor((remaining % 60_000) / 1000);

    this.refs.days.textContent = String(days).padStart(2, '0');
    this.refs.hours.textContent = String(hours).padStart(2, '0');
    this.refs.minutes.textContent = String(minutes).padStart(2, '0');
    this.refs.seconds.textContent = String(seconds).padStart(2, '0');
  };

  #hide = () => {
    this.closest('.countdown-bar')?.remove();

    // The header group's height is only measured once on page load. Removing
    // this bar shrinks the group, so the cached height (used to position the
    // hero under a transparent header) must be recalculated or the hero ends
    // up shifted out of view.
    updateAllHeaderCustomProperties();
  };
}

if (!customElements.get('countdown-bar-component')) {
  customElements.define('countdown-bar-component', CountdownBarComponent);
}
